import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, ArrowUp, ArrowDown, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const variantLabel = (options) => {
  const vals = Object.entries(options || {});
  if (vals.length === 0) return 'Default';
  return vals.map(([k, v]) => `${k}: ${v}`).join(' • ');
};

export default function AdminInventory() {
  const { getAuthHeaders } = useApp();
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [formData, setFormData] = useState({
    product_id: '',
    variant_id: '',
    quantity_change: '',
    reason: '',
    supplier_id: ''
  });
  const [restockQtys, setRestockQtys] = useState({});
  const [restockingKey, setRestockingKey] = useState(null);

  const fetchData = async () => {
    try {
      const [productsRes, suppliersRes, logsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/products`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/admin/suppliers`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/admin/inventory/logs`, { headers: getAuthHeaders() })
      ]);
      setProducts(productsRes.data);
      setSuppliers(suppliersRes.data);
      setLogs(logsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [getAuthHeaders]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedProduct = products.find((p) => p.id === formData.product_id);
  const productVariants = selectedProduct?.variants || [];
  const needsVariant = productVariants.length > 1;

  const handleProductChange = (value) => {
    const prod = products.find((p) => p.id === value);
    const vars = prod?.variants || [];
    setFormData({
      ...formData,
      product_id: value,
      variant_id: vars.length === 1 ? vars[0].id : ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (needsVariant && !formData.variant_id) {
      toast.error('Please select a variant');
      return;
    }

    try {
      await axios.post(`${API_URL}/admin/inventory/update`, {
        product_id: formData.product_id,
        variant_id: formData.variant_id || (productVariants[0]?.id ?? null),
        quantity_change: parseInt(formData.quantity_change, 10),
        reason: formData.reason,
        supplier_id: formData.supplier_id || null
      }, {
        headers: getAuthHeaders()
      });

      toast.success('Inventory updated successfully');
      setDialogOpen(false);
      setFormData({ product_id: '', variant_id: '', quantity_change: '', reason: '', supplier_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update inventory');
    }
  };

  // Flatten products -> variant rows for stock views
  const variantRows = products.flatMap((p) =>
    (p.variants || []).map((v) => ({
      productId: p.id,
      variantId: v.id,
      productName: p.name,
      productImage: p.images?.[0],
      category: p.category,
      isActive: p.is_active,
      hasOptions: (p.option_types || []).length > 0,
      label: variantLabel(v.options),
      sku: v.sku || p.sku || '—',
      stock: v.stock
    }))
  );
  const lowStockRows = variantRows.filter((r) => r.stock < 10 && r.isActive);

  const handleQuickRestock = async (row) => {
    const key = `${row.productId}-${row.variantId}`;
    const qty = parseInt(restockQtys[key] ?? 10, 10);
    if (!qty || qty <= 0) {
      toast.error('Enter a quantity greater than 0');
      return;
    }
    setRestockingKey(key);
    try {
      await axios.post(`${API_URL}/admin/inventory/update`, {
        product_id: row.productId,
        variant_id: row.variantId,
        quantity_change: qty,
        reason: 'Restock',
        supplier_id: null
      }, {
        headers: getAuthHeaders()
      });
      toast.success(`Restocked ${row.productName} (+${qty})`);
      setRestockQtys((prev) => ({ ...prev, [key]: '' }));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restock');
    } finally {
      setRestockingKey(null);
    }
  };

  return (
    <div className="p-6" data-testid="admin-inventory">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground" style={{ fontFamily: "'Playfair Display', serif" }}>
            Inventory
          </h1>
          <p className="text-muted-foreground mt-1">Manage stock levels</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-full" data-testid="update-stock-btn">
              <Plus className="h-4 w-4 mr-2" />
              Update Stock
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>
                Update Inventory
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="product_id">Product *</Label>
                <Select value={formData.product_id} onValueChange={handleProductChange}>
                  <SelectTrigger className="mt-1" data-testid="inventory-product-select">
                    <SelectValue placeholder="Select product" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((product) => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.name} (Total: {product.total_stock})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {needsVariant && (
                <div>
                  <Label htmlFor="variant_id">Variant *</Label>
                  <Select value={formData.variant_id} onValueChange={(value) => setFormData({ ...formData, variant_id: value })}>
                    <SelectTrigger className="mt-1" data-testid="inventory-variant-select">
                      <SelectValue placeholder="Select variant" />
                    </SelectTrigger>
                    <SelectContent>
                      {productVariants.map((v) => (
                        <SelectItem key={v.id} value={v.id}>
                          {variantLabel(v.options)} (Stock: {v.stock})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label htmlFor="quantity_change">Quantity Change *</Label>
                <Input
                  id="quantity_change"
                  type="number"
                  value={formData.quantity_change}
                  onChange={(e) => setFormData({ ...formData, quantity_change: e.target.value })}
                  placeholder="Enter positive to add, negative to remove"
                  required
                  className="mt-1"
                  data-testid="inventory-quantity-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Use positive numbers to add stock, negative to remove
                </p>
              </div>

              <div>
                <Label htmlFor="reason">Reason *</Label>
                <Textarea
                  id="reason"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  placeholder="e.g., New shipment arrived, Damaged goods, etc."
                  required
                  rows={2}
                  className="mt-1"
                  data-testid="inventory-reason-input"
                />
              </div>

              <div>
                <Label htmlFor="supplier_id">Supplier (Optional)</Label>
                <Select value={formData.supplier_id} onValueChange={(value) => setFormData({ ...formData, supplier_id: value })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select supplier" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white" data-testid="submit-inventory-btn">
                  Update Stock
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Low Stock Alert */}
        <div className="bg-white rounded-xl border border-border">
          <div className="p-6 border-b border-border">
            <h2 className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              Low Stock Alert
            </h2>
          </div>
          <div className="p-6">
            {lowStockRows.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">
                All products are well stocked
              </p>
            ) : (
              <div className="space-y-4">
                {lowStockRows.map((row, i) => {
                  const key = `${row.productId}-${row.variantId}`;
                  return (
                    <div key={key} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200" data-testid={`low-stock-row-${i}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <img
                          src={row.productImage || 'https://images.unsplash.com/photo-1559709451-1b5dc3c0038c?w=60'}
                          alt={row.productName}
                          className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                        />
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{row.productName}</p>
                          {row.hasOptions && <p className="text-xs text-muted-foreground truncate">{row.label}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`font-bold text-sm ${row.stock === 0 ? 'text-red-600' : 'text-amber-600'}`}>
                          {row.stock} left
                        </span>
                        <input
                          type="number"
                          min="1"
                          value={restockQtys[key] ?? ''}
                          onChange={(e) => setRestockQtys((prev) => ({ ...prev, [key]: e.target.value }))}
                          placeholder="10"
                          className="w-16 h-9 rounded-md border border-input bg-background px-2 text-sm"
                          data-testid={`restock-qty-${i}`}
                        />
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => handleQuickRestock(row)}
                          disabled={restockingKey === key}
                          className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white h-9"
                          data-testid={`restock-btn-${i}`}
                        >
                          {restockingKey === key ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <RefreshCw className="h-4 w-4 mr-1" />
                              Restock
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-xl border border-border">
          <div className="p-6 border-b border-border">
            <h2 className="text-lg font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>
              Recent Activity
            </h2>
          </div>
          <div className="p-6">
            {logs.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">
                No inventory activity yet
              </p>
            ) : (
              <div className="space-y-4 max-h-[400px] overflow-y-auto">
                {logs.slice(0, 10).map((log) => (
                  <div key={log.id} className="flex items-start gap-3 pb-4 border-b border-border last:border-0">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      log.quantity_change > 0 ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      {log.quantity_change > 0 ? (
                        <ArrowUp className="h-4 w-4 text-green-600" />
                      ) : (
                        <ArrowDown className="h-4 w-4 text-red-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">
                        {log.product_name}
                        {log.variant_label && log.variant_label !== 'Default' && (
                          <span className="text-muted-foreground font-normal"> — {log.variant_label}</span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">{log.reason}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(log.created_at).toLocaleString('en-NZ')}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`font-bold ${log.quantity_change > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {log.quantity_change > 0 ? '+' : ''}{log.quantity_change}
                      </span>
                      <p className="text-xs text-muted-foreground">
                        New: {log.new_stock}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* All Variants Stock */}
      <div className="mt-6 bg-white rounded-xl border border-border">
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>
            Stock Overview
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">Product</th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">Variant</th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">SKU</th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">Category</th>
                <th className="text-right p-4 text-sm font-medium text-muted-foreground">Stock</th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {variantRows.map((row, i) => (
                <tr key={`${row.productId}-${i}`} className="border-b border-border last:border-0 hover:bg-muted/30">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <img
                        src={row.productImage || 'https://images.unsplash.com/photo-1559709451-1b5dc3c0038c?w=60'}
                        alt={row.productName}
                        className="w-10 h-10 rounded-lg object-cover"
                      />
                      <p className="font-medium">{row.productName}</p>
                    </div>
                  </td>
                  <td className="p-4 text-sm">{row.hasOptions ? row.label : '—'}</td>
                  <td className="p-4 mono text-sm">{row.sku}</td>
                  <td className="p-4 text-sm">{row.category}</td>
                  <td className="p-4 text-right font-bold">{row.stock}</td>
                  <td className="p-4">
                    {row.stock === 0 ? (
                      <span className="status-badge cancelled">Out of Stock</span>
                    ) : row.stock < 10 ? (
                      <span className="status-badge pending">Low Stock</span>
                    ) : (
                      <span className="status-badge delivered">In Stock</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
